import isEmpty from 'lodash/isEmpty'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { usePortal } from 'modules/core/lib/portalContext'

import documents from 'modules/documents/store/collections/documents'
import type DocumentForm from 'modules/timeoff/lib/DocumentForm'
import type { CustomFieldAttributes } from 'modules/timeoff/lib/DocumentForm'
import type LeaveForm from 'modules/timeoff/lib/LeaveForm'
import { useLeaves } from 'modules/timeoff/lib/LeavesContextProvider'
import type Leave from 'modules/timeoff/store/models/Leave'

interface Props {
  leave: Leave
  form: LeaveForm
  documentForm: DocumentForm
  canUpdateLeave: boolean
  fetchStats: (leave: Leave, employeeId: number) => void
  onChange?: () => void
  getFieldsValues: () => CustomFieldAttributes[]
  leaveEmployeeId: number
  sickFolderId: number
}

const useUpdateLeave = ({
  leave,
  form,
  documentForm,
  canUpdateLeave,
  fetchStats,
  onChange,
  getFieldsValues,
  leaveEmployeeId,
  sickFolderId,
}: Props) => {
  const { fetchData } = useLeaves()
  const { closePortal } = usePortal()

  const saveDocument = async () => {
    const customFieldsValues = await getFieldsValues()

    if (isEmpty(documentForm.get('file').value)) return

    documentForm.setDocumentValues(leave, customFieldsValues, leaveEmployeeId, sickFolderId)
    await documentForm.create(documents)
  }

  const updateLeave = async () => (canUpdateLeave ? await form.save(leave) : true)

  const { performAction: onSubmit, running: updateLoading } = useAsyncAction(async () => {
    const canUpdate = await updateLeave()

    if (canUpdate) {
      await saveDocument()
      fetchStats(leave, leaveEmployeeId)
      fetchData && fetchData(leaveEmployeeId)
      closePortal()

      if (onChange) onChange()
    }
  })

  return {
    onSubmit,
    updateLoading,
  }
}

export default useUpdateLeave
